/**
 * Partner-tier copy — labels, tooltips and messages (Partner tier guide §11, plus
 * the strings the earlier sections define inline).
 *
 * Single source so wording can't drift between screens. Where §11 and an earlier
 * section word the same message differently, §11 wins. Strings that name the
 * partner or company are functions: the guide's "Acme Consulting" / "Company A"
 * are placeholders for the real names.
 *
 * Messages about an arbitrary user are written without gendered pronouns (the
 * guide's examples use "she"; we don't know who the user is).
 */

const partnerOr = (name) => name || 'your partner';
const companyOr = (name) => name || 'this company';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ── Tenancy / session ─────────────────────────────────────────────────────────
export const TENANCY_COPY = {
  directUsers: 'Direct users',
  directUsersHint: (partner) => `Direct users — ${partnerOr(partner)}'s own staff.`,
  directUserOption: (partner) => `Direct user (${partnerOr(partner)} staff)`,
  ownedByYou: (partner) => `${partnerOr(partner)} (yours)`,
  managedBy: (partner) => `Managed by ${partnerOr(partner)}`,
  partnerAdminChip: (partner) => `${partnerOr(partner)} · Partner admin`,
  partnerStaffChip: (partner) => `${partnerOr(partner)} · Partner staff`,
  platformDirect: 'Platform-direct',
  platformActor: 'Platform',
  deactivated: 'Deactivated',
};

// ── Lockout screens (§9) ──────────────────────────────────────────────────────
export const LOCKOUT_COPY = {
  partner: {
    title: 'Access paused',
    body: "Your organisation's access has been paused. Please contact your account manager.",
  },
  company: {
    title: 'Access paused',
    body: "Your company's access has been paused. Please contact your administrator.",
  },
  user: {
    title: 'Account deactivated',
    body: 'Your account has been deactivated. Please contact your administrator.',
  },
};

// ── Roles ─────────────────────────────────────────────────────────────────────
export const ROLE_COPY = {
  partnerAdmin: 'Partner admin',
  companyAdmin: 'Company admin',
  partnerAdminTooltip: (partner) =>
    `Can manage all of ${partnerOr(partner)}'s companies, users, limits, keys, sharing and usage.`,
  companyAdminTooltip: 'Can manage users, roles and settings of this company only.',
  directUsersRoles: "Direct users' roles",
  managedByPlatform: 'Managed by the platform.',
  managedByPartner: 'Managed by your partner.',
};

// ── Platform → Partners (§4) ──────────────────────────────────────────────────
export const PLATFORM_PARTNER_COPY = {
  statusTooltip:
    'Deactivating blocks everyone under this partner until reactivated. Nothing is deleted.',
  deactivateTitle: (partner) => `Deactivate ${partner}?`,
  deactivateBody: (companyCount) =>
    `Everyone under this partner — its staff and all users of its ${plural(
      companyCount,
      'company',
      'companies'
    )} — will be signed out and blocked until you reactivate it. Nothing is deleted.`,
  deactivateConfirm: 'Deactivate partner',
  firstAdminSection: 'First partner admin (optional)',
  firstAdminFailed:
    "The partner was created, but its first admin could not be. Add one from the partner's Admins tab.",
  attachBody: (company, partner) =>
    `Move ${company} under ${partner}? Its users will start seeing what ${partner} shares with all its companies. Its limits will be capped by ${partner}'s limits. For LLM keys it will fall back to ${partner}'s keys (unless you turn that off per provider), instead of its own platform fallback setting.`,
  detachBody: (company, partner) =>
    `Detach ${company} from ${partner}? Its users lose everything ${partner} shared with them. It stops using ${partner}'s LLM keys and goes back to its own platform settings, which may block LLM access until you configure them. Its past usage stays in ${partner}'s reports.`,
  homeCompanyStatus:
    "A partner's home company is activated and deactivated with its partner. Use the partner's status toggle instead.",
  partnerFallbackLabel: 'Partner may fall back to platform keys',
  partnerFallbackTooltip:
    "When the partner has no key, allow it and its companies to use the platform's key.",
  companyFallbackUnderPartner:
    'Not used while this company belongs to a partner — it falls back through the partner instead.',
};

// ── Feature limits (§4.4, §5.3, §7.1) ─────────────────────────────────────────
export const LIMITS_COPY = {
  companyGroupTitle: 'Features its companies may use',
  companyGroupHelp: 'The most any company under this partner can have.',
  partnerGroupTitle: 'Partner powers',
  partnerGroupHelp: "What the partner's admins can do.",
  lockedAdminKey: "Partner admins always keep this so they can't be locked out.",
  usageRunContentWarning:
    "Reading other users' prompts and answers. With this on, the partner's own admins can see it for the partner's staff, and the platform can enable it for individual companies under this partner (Companies → Feature limits). A company only gets it if it's on here too. Partners can never enable it themselves.",
  partnerUsageRunContentWarning:
    "Lets partner admins read prompt and answer text of runs in all the partner's companies.",
  platformSaved:
    'Limits updated. Features you removed are unavailable to every company under this partner immediately.',
  partnerEditorHelp: (company) =>
    `Choose which features ${companyOr(company)} can use. You can only offer what your own plan includes.`,
  notInPlan: 'Not included in your partner plan. Contact the platform to enable it.',
  noLongerInPlan: 'No longer in your partner plan.',
  enabledByPlatform: 'Enabled by the platform. Only the platform can change this.',
  keysNotAllowed: (keys) =>
    `These features aren't in your plan any more: ${keys}. Refresh and try again.`,
  partnerSaved: (company) =>
    `${companyOr(company)}'s features updated. Its users see the change immediately.`,
  companyAdminHelp: (partner) => `Set by ${partnerOr(partner)} (your partner) and the platform.`,
};

// ── LLM keys (§4.6, §5.5, §8.2) ───────────────────────────────────────────────
export const KEYS_COPY = {
  saved: 'Saved. Changes apply within 5 minutes.',
  inheritToggle: (partner, company) =>
    `Use ${partnerOr(partner)}'s key when ${companyOr(company)} has none.`,
  // Effective-state badges for a partner's client company (§5.5 truth table).
  badgeOwn: (last4) => (last4 ? `Own key •••• ${last4}` : 'Own key'),
  badgePartner: 'Uses partner key',
  badgePlatform: 'Uses platform key',
  badgeBlocked: 'Blocked',
  tipOwn: (company) => `${companyOr(company)} uses its own key.`,
  tipInheritOff: (company) =>
    `Inheritance is off and ${companyOr(company)} has no key. Its users can't use this provider unless they add their own key.`,
  tipPartner: (partner, company) => `${companyOr(company)} uses ${partnerOr(partner)}'s key.`,
  tipPlatform: (partner, company) =>
    `Neither ${companyOr(company)} nor ${partnerOr(partner)} has a key; the platform's key is used.`,
  tipBlocked: 'No key is available. Add a key for the company or for the partner.',
  // The partner's own keys screen (fallback is platform-controlled).
  partnerFallbackReadOnly: (on) => `Platform fallback: ${on ? 'On' : 'Off'} (set by the platform)`,
  partnerFallbackReadOnlyTooltip:
    "When you have no key for this provider, the platform's key is used if the platform allows it.",
  clearPartnerKey: (partner, provider) =>
    `Remove ${partnerOr(partner)}'s ${provider} key? Direct users and every company inheriting it will fall back to the platform key if allowed, or lose ${provider} access.`,
  // What a client company's admin sees for a provider with no own key.
  fromPartner: (partner) => `From ${partnerOr(partner)}`,
  fromPartnerTooltip: 'Your partner provides this provider unless you add your own key.',
  turnedOffByPartner: (partner) => `Turned off by ${partnerOr(partner)}`,
  turnedOffByPartnerTooltip:
    'Your partner has turned this provider off for your company. Add your own key or ask your partner.',
  // My LLM keys — effective source labels.
  sourcePartner: (partner) => `${partnerOr(partner)}'s key`,
};

// ── Partner admin: companies & users (§5.2, §5.4) ─────────────────────────────
export const PARTNER_ADMIN_COPY = {
  nameTaken: 'That name is taken. Try another.',
  addAdminFor: (company) => `Add an admin for ${companyOr(company)}`,
  reviewLimits: 'Review feature limits',
  deactivateCompany: (company) =>
    `Deactivate ${companyOr(company)}? Its users are blocked until you reactivate it. Nothing is deleted.`,
  moveUser: (user, company) =>
    `Move ${user} to ${company}? They lose their admin role and any custom roles of their current company. Their past usage stays with the old company.`,
  deactivateUser: (user) =>
    `Deactivate ${user}? They are signed out and blocked until you reactivate them.`,
  lastPartnerAdmin: (partner) =>
    `${partnerOr(partner)} needs at least one active partner admin. Make someone else an admin first.`,
  higherTierUser: "You can't change this user — they have higher access than you.",
  onlyDirectUsersPartnerAdmin: 'Only direct users can be partner admins.',
  directUsersNotCompanyAdmin: "Direct users can't be company admins — use Partner admin instead.",
  noLongerInPartner: (partner) =>
    `That company or user is no longer part of ${partnerOr(partner)}.`,
};

// ── Sharing (§6, §7.3, §8.1) ──────────────────────────────────────────────────
export const SHARING_COPY = {
  modeWithin: (company) => `Within ${companyOr(company)}`,
  modeAcross: (partner) => `Across ${partnerOr(partner)}'s companies`,
  targetAll: (partner) => `All ${partnerOr(partner)} companies`,
  targetAllHelp: (partner) =>
    `Everyone in all of ${partnerOr(partner)}'s companies — including companies and people you add later.`,
  targetCompanies: 'Specific companies',
  targetCompaniesHelp: 'Everyone in the selected companies, including people added later.',
  targetPeople: 'Specific people',
  readHelp: 'Can use it: run the agent, search the folder.',
  writeHelp:
    "Can also change it: edit the agent, upload into the folder. Uploads go into the owner's knowledge base.",
  memoryPerUser: 'Each person has their own conversation history',
  composioWithoutAuth: 'Each person connects their own account',
  composioWithAuth: "Use the owner's connection",
  ownerConnectionWarning: "Everyone you share with will act through the owner's connected account.",
  customToolInclude: (tool) => `Include '${tool}' (uses the owner's stored credentials)`,
  customToolTooltip:
    'Custom tools always run with the owner\'s saved credentials. Only include this if every recipient company may use them.',
  runnerPays: "People in other companies run this agent on their own company's AI keys.",
  clientOwnedBanner: (company) =>
    `Owned by ${companyOr(company)}. ${companyOr(company)}'s admins can see and revoke this sharing.`,
  shareAcross: 'Share across companies',
  groupWithin: (company) => `Within ${companyOr(company)}`,
  groupAcross: (partner) => `Across companies — shared by ${partnerOr(partner)}`,
  revokeAsOwner: (resource, partner) =>
    `Stop sharing ${resource} with ${partnerOr(partner)}'s companies? People in those companies lose access immediately. ${partnerOr(partner)} can share it again later.`,
  sharedWithMeBadge: (partner) => `From ${partnerOr(partner)}`,
  sharedWithMeTooltip: "Shared by your partner. Runs use your company's AI keys.",
  ownerCompanyBadge: (partner, n) => `Shared by ${partnerOr(partner)} (${n})`,
  ownerCompanyBadgeTooltip:
    'Your partner shared this with other companies. Click to see who has access or to revoke.',
  runsOnYourKeys: "This agent runs on your company's AI keys.",
};

// ── Usage (§5.7) ──────────────────────────────────────────────────────────────
export const USAGE_COPY = {
  attribution: 'Usage is counted under the partner that owned the company at the time of each run.',
  partnerRedacted:
    'Prompt and answer text is hidden. Your platform administrator can enable it for your partner.',
  partnerEmpty: "No usage yet. Usage appears here once your companies' users start running agents.",
};
